import { PaginationOptions } from 'types/api';
import { invalidate, invalidateStartsWith } from 'hooks/useSWR';
import { withPagination } from 'apis/utils';
import config from 'config';
import { Message } from './conversationTypes';
import { conversationMeKey } from './useConversationMe';
import { useEffect } from 'react';
import { reloadConversationList } from './useConversationList';
import usePaginatedResource from 'util/resource/usePaginatedResource';

export const companyConversationMessagesKey = (companyId?: number, paginationOptions?: PaginationOptions) => {
  if (!companyId || !paginationOptions) return undefined;
  return withPagination(`${config.COMPANIES_API_URL}/companies/${companyId}/conversations`, paginationOptions);
};

export const reloadCompanyConversationMessages = (companyId?: number) => {
  if (!companyId) return;
  invalidateStartsWith([`${config.COMPANIES_API_URL}/companies/${companyId}/conversations`]);
};

export const useCompanyConversationMessages = (companyId: number | undefined, initialLimit: number) => {
  const [state, reload, onSeeMore] = usePaginatedResource<Message>(
    (paginationOptions: PaginationOptions) => {
      if (!companyId) return '';
      return companyConversationMessagesKey(companyId, paginationOptions) || '';
    },
    { limit: initialLimit, page: 1 },
    undefined,
    {
      mode: 'append',
      uniqueSelector: (m: Message) => m.id,
      deps: [companyId], // Reset when company changes
    },
  );

  // Preserve side effects: invalidate related caches when page 1 loads
  useEffect(() => {
    if (state.state === 'fetched' && companyId && state.resource.page === 1) {
      invalidate(conversationMeKey);
      reloadConversationList();
    }
  }, [state.state, companyId, state.state === 'fetched' && state.resource.page]);

  return [state, reload, onSeeMore] as const;
};
